import { useState } from 'react';
import { useReglages } from '../../api/reglages.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { _inp } from '../../components/ui/Inputs.jsx';
import { articlesColles } from '../../lib/coller-articles';
import { DEVISE, fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { uid } from '../../lib/utils';
import { C } from '../../theme/palette';
import { CLE_MODELES } from '../modeles/ModelesPage.jsx';

/**
 * « 📋 Coller une liste d'articles » : la liste des Notes du téléphone, de
 * Word ou d'Excel, collée telle quelle, devient les lignes de l'offre ou de la
 * facture — nom, quantité, unité, prix.
 *
 * <p>Les prix collés sont des prix de vente hors taxe, ou des prix publics TVA
 * comprise ramenés hors taxe. La liste lue se vérifie avant d'être ajoutée.
 * Les prix d'un client se gardent comme modèle à son nom, rechargé à sa
 * prochaine commande.
 *
 * @param {object} props propriétés
 * @param {object} props.form la pièce en cours (lignes, client, taux de TVA)
 * @param {Function} props.setForm met la pièce à jour
 * @param {object[]} [props.catalogue] articles de l'entreprise
 * @param {object[]} [props.clients] clients
 * @param {boolean} [props.deplie] déjà ouverte (outil choisi dans « ajouter un article »)
 * @param {boolean} [props.estMag] magasin : les articles gardent leur nom ; en
 *   bâtiment, de quoi il s'agit en désignation, ce qu'on fait en description
 * @returns {import('react').ReactElement} la case
 */
export function CollerArticles({ form, setForm, catalogue, clients, deplie, estMag }) {
  const { reglageJson, setReglageJson } = useReglages();
  const [ouvert, setOuvert] = useState(!!deplie);
  const [texte, setTexte] = useState('');
  const [prix, setPrix] = useState('ht');
  const [lues, setLues] = useState(null);
  const [garder, setGarder] = useState(false);
  const client = (clients || []).find((c) => String(c.id) === String(form.clientId));
  const tva = form.tvaPct ?? 8.1;

  const lire = () => {
    const l = articlesColles(texte, { prix, tva, batiment: !estMag });
    if (!l.length) {
      window.alert(
        tr('Aucun article reconnu. Collez une ligne par article, le prix en fin de ligne.'),
      );
      return;
    }
    setLues(l);
  };

  const ajouter = () => {
    const parNom = new Map(
      (catalogue || []).map((a) => [
        String(a.d || a.nom || '')
          .trim()
          .toLowerCase(),
        a,
      ]),
    );
    const lignes = lues.map(({ d, sd, q, u, p }) => {
      // L'article déjà au catalogue garde sa référence : le stock suit.
      const a = parNom.get(d.toLowerCase());
      return {
        id: uid(),
        d,
        sd: sd || '',
        u,
        q,
        p,
        rem: 0,
        taxe: true,
        // Le catalogue range l'article sous son identifiant (ou sa référence
        // pour le stock) : c'est lui que la ligne garde.
        ...(a?.id != null ? { ref: a.id } : a?.ref != null ? { ref: a.ref } : {}),
        // Le taux propre de l'article connu (magasin : 2.6 % sur l'alimentation).
        ...(a?.tva != null ? { tva: +a.tva } : {}),
      };
    });
    setForm((p) => ({ ...p, lignes: [...(p.lignes || []), ...lignes] }));
    if (garder && client) {
      const nom = trf('Prix — {0}', client.nom);
      const propres = reglageJson(CLE_MODELES, []).filter((m) => m.nom !== nom);
      setReglageJson(CLE_MODELES, [
        ...propres,
        {
          nom,
          lignes: lignes
            .filter((l) => l.p > 0)
            .map(({ d, u, q, p, ref }) => ({ d, u, q, p, rem: 0, ref })),
        },
      ]);
    }
    window.alert(
      trf('✓ {0} article(s) ajouté(s). Vérifie les quantités et les prix.', lignes.length) +
        (garder && client
          ? '\n' + trf('Prix gardés dans le modèle « {0} ».', trf('Prix — {0}', client.nom))
          : ''),
    );
    setTexte('');
    setLues(null);
    setGarder(false);
    setOuvert(false);
  };

  const sansPrix = (lues || []).filter((l) => l.sansPrix).length;
  const total = (lues || []).reduce((s, l) => s + l.q * l.p, 0);

  if (!ouvert) {
    return (
      <div style={{ marginBottom: 12 }}>
        <Btn
          label="📋 Coller une liste d'articles (Notes, Word, Excel)"
          color={C.teal}
          ghost
          full
          onClick={() => setOuvert(true)}
        />
      </div>
    );
  }
  return (
    <div
      style={{
        background: '#ecfeff',
        border: '1px solid #a5f3fc',
        borderRadius: 11,
        padding: 12,
        marginBottom: 12,
      }}
    >
      <div style={{ fontSize: 11.5, fontWeight: 800, color: '#155e75', marginBottom: 3 }}>
        📋 {tr('Coller une liste d’articles')}
      </div>
      <div style={{ fontSize: 9.5, color: '#0e7490', marginBottom: 8, lineHeight: 1.45 }}>
        {client
          ? trf('Client : {0}', client.nom) + ' · '
          : tr('Choisissez d’abord le client.') + ' '}
        {tr('Une ligne par article, le prix à la fin : « Silicone blanc 310 ml   2 x 8.90 ».')}{' '}
        {tr(
          'Ou un bloc par article : le nom, puis la description, le prix à la fin (« 50 CHF m2 »), une ligne vide entre deux articles.',
        )}
      </div>
      {!lues ? (
        <>
          <textarea
            aria-label={tr('Liste d’articles collée')}
            rows={14}
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            placeholder={
              'Vis inox 4x40 boîte 200   12.50\nSilicone blanc 310 ml   2 x 8.90\n\nDémolition\nDépose de l’ancien carrelage, y compris évacuation. 50 CHF m2'
            }
            style={{ ..._inp, fontFamily: 'inherit', resize: 'vertical', minHeight: 260 }}
          />
          <div
            style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '8px 0', fontSize: 11 }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
              <input
                type="radio"
                name="prix-colles"
                checked={prix === 'ht'}
                onChange={() => setPrix('ht')}
              />
              {tr('Prix de vente (hors TVA)')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
              <input
                type="radio"
                name="prix-colles"
                checked={prix === 'ttc'}
                onChange={() => setPrix('ttc')}
              />
              {trf('Prix public (TVA {0} % comprise)', tva)}
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Btn label="Lire la liste" color={C.teal} full onClick={lire} />
            </div>
            <Btn label="Fermer" ghost color={C.muted} onClick={() => setOuvert(false)} />
          </div>
        </>
      ) : (
        <>
          <div style={{ background: C.white, borderRadius: 9, border: '1px solid ' + C.border }}>
            {lues.map((l, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '7px 9px',
                  borderBottom: i < lues.length - 1 ? '1px solid ' + C.border : 'none',
                  fontSize: 11,
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <b>{l.d}</b>
                  {l.sd && (
                    <span style={{ display: 'block', color: C.muted, fontSize: 10, marginTop: 2 }}>
                      {l.sd}
                    </span>
                  )}
                </span>
                <span style={{ color: C.muted, whiteSpace: 'nowrap' }}>
                  {fmt(l.q).replace(/\.00$/, '')} {l.u}
                </span>
                <b style={{ whiteSpace: 'nowrap', color: l.sansPrix ? C.red : C.ink }}>
                  {l.sansPrix ? tr('prix ?') : DEVISE + ' ' + fmt(l.p)}
                </b>
                <button
                  type="button"
                  aria-label={tr('Retirer cet article')}
                  onClick={() => setLues((p) => p.filter((_, j) => j !== i))}
                  style={{
                    background: C.redL,
                    border: 'none',
                    borderRadius: 7,
                    color: C.red,
                    padding: '3px 7px',
                    cursor: 'pointer',
                  }}
                >
                  🗑
                </button>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 10.5, color: '#155e75', margin: '7px 0', lineHeight: 1.45 }}>
            {trf('{0} article(s) · total hors TVA {1}', lues.length, DEVISE + ' ' + fmt(total))}
            {prix === 'ttc' ? ' · ' + tr('prix publics ramenés hors TVA') : ''}
          </div>
          {sansPrix > 0 && (
            <div
              role="alert"
              style={{ fontSize: 10.5, color: C.red, fontWeight: 700, marginBottom: 7 }}
            >
              ⚠{' '}
              {trf('{0} ligne(s) sans prix lu : ajoutez le prix après, ou retirez-les.', sansPrix)}
            </div>
          )}
          {client && (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                fontSize: 11,
                marginBottom: 8,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={garder}
                onChange={(e) => setGarder(e.target.checked)}
              />
              {trf('Garder ces prix pour {0} (modèle à son nom)', client.nom)}
            </label>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Btn
                label={trf('✓ Ajouter ces {0} articles', lues.length)}
                color={C.green}
                full
                disabled={!lues.length}
                onClick={ajouter}
              />
            </div>
            <Btn label="← Corriger" ghost color={C.muted} onClick={() => setLues(null)} />
          </div>
        </>
      )}
    </div>
  );
}
